import { IDirective, IDirectiveFactory, IScope } from "angular";
import { GoalService } from "@gtmhub/goals/goalService";
import Chartist, { ILineChartOptions } from "chartist";
import { IMetricSnapshot, IMetric } from "@gtmhub/okrs/metrics";
import { IGoal } from "@gtmhub/goals/models";
import dayjs from "dayjs";

interface TrendChartAntiKrScope extends IScope {
  goal: IGoal;
  metric: IMetric;
  id: string;
}

export class TrendChartAntiKr implements IDirective {
  scope = {
    metric: "=",
    goal: "=",
    id: "@",
  };
  restrict = "E";
  transclude = true;
  template = "<div id='metric-trend-{{id}}' class='metric-trend-chart'></div>";

  constructor(private goalService: GoalService) {}

  // eslint-disable-next-line sonarjs/cognitive-complexity
  link(scope: TrendChartAntiKrScope): void {
    // needed to calculate the space above/below greatest/lowest number
    // and display them all
    let maxValue: number;
    let minValue: number;
    let difference: number;

    const dateFormatStrings = {
      ddmmm: "DD MMM",
      ddmmmyy: "DD MMM YY",
    };

    // data object to pass when drawing chart
    const data = {
      labels: [],
      series: [[]],
    };

    // index of actual line chart
    const actualDataIndex = 0;

    maxValue = Math.max(scope.metric.initialValue, scope.metric.target);
    minValue = Math.min(scope.metric.initialValue, scope.metric.target);

    // if there is a critical target update max and min
    if (scope.metric.critical || scope.metric.critical === 0) {
      maxValue = Math.max(maxValue, scope.metric.critical);
      minValue = Math.min(minValue, scope.metric.critical);
    }

    const addThosandSeparator = (value: number): string => {
      return value.toLocaleString("en");
    };

    const calculateMaxActualLength = (metricHistory: IMetricSnapshot[]): number => {
      if (!Array.isArray(metricHistory)) {
        return 0;
      }

      return metricHistory.reduce((maxActualLength: number, currentSnapshot: IMetricSnapshot): number => {
        const currentSnapshotActualLength: number = addThosandSeparator(currentSnapshot.metricValue).length;

        return currentSnapshotActualLength > maxActualLength ? currentSnapshotActualLength : maxActualLength;
      }, 0);
    };

    // wrapped in a method because logic needs the goal the metric belongs to
    // in collections such as urgentGoals we may not have a goal passed as directive param
    const configureChartParamsAndDraw = () => {
      // +1 added to difference to include last day of the period
      const dates = scope.metric.metricHistory.map((history) => dayjs(history.updatedAt));
      const min = dayjs.min(dates);
      const max = dayjs(scope.goal.dateTo);
      const duration = max.diff(min, "days") + 1;

      // calculating year difference of session's start and end
      const yearDifference: number = dayjs(max).diff(min, "years");
      // setting date format to display year if session starts and ends in different years
      const dateFormatString = yearDifference > 0 ? dateFormatStrings.ddmmmyy : dateFormatStrings.ddmmm;

      // interval of labels placed on x axis
      const interval: number = Math.round(duration / 4);
      // object to identify the correct date/index where to place the check-in value
      const dateDataIndexMap: { [date: string]: number } = {};

      // setting labels and date-index map
      for (let i = 0; i < duration; i++) {
        const beautifiedDate = dayjs(min).add(i, "days").format(dateFormatString);
        dateDataIndexMap[beautifiedDate] = i;
        data.labels.push(beautifiedDate);
      }

      // setting the start of the progress line to be where the start of the period and initial value cross
      data.series[actualDataIndex].push(scope.metric.initialValue);

      // setting values of updates --> progress line
      // and updating max and min for the last time
      for (const record of scope.metric.metricHistory) {
        const beautifiedDate = dayjs(record.updatedAt).format(dateFormatString);
        const index = dateDataIndexMap[beautifiedDate];

        data.series[actualDataIndex][index] = record.metricValue;

        maxValue = Math.max(maxValue, record.metricValue);
        minValue = Math.min(minValue, record.metricValue);
      }

      difference = Math.abs(maxValue - minValue);

      const chartTop = Math.ceil(maxValue + difference / 5);
      const chartBottom = Math.floor(minValue - difference / 5);

      const chartWidth = 460;

      const maxActualValueLength: number = calculateMaxActualLength(scope.metric.metricHistory);
      const metricCriticalValueLength: number = scope.metric.critical ? addThosandSeparator(scope.metric.critical).length : 0;
      const maxValueLength: number = Math.max(
        addThosandSeparator(scope.metric.target).length,
        addThosandSeparator(scope.metric.initialValue).length,
        metricCriticalValueLength,
        maxActualValueLength
      );
      const [safetyPaddingYPx, digitSizePx] = [5, 5];

      const options: ILineChartOptions = {
        width: chartWidth + "px",
        chartPadding: {
          top: 15,
          right: 40,
          bottom: 5,
          left: 20,
        },
        showPoint: true,
        axisX: {
          labelInterpolationFnc: function skipLabels(value, index) {
            // include last date goal.dateTo in x axis
            if (index + 1 === duration) {
              return value;
            }

            if (index % interval === 0) {
              return value;
            }

            return null;
          },
        },
        axisY: {
          high: chartTop,
          low: chartBottom,
          // the offset of the labels to the chart area
          offset: safetyPaddingYPx + maxValueLength * digitSizePx,
          // position where labels are placed. Can be set to `start` or `end` where `start` is equivalent to left or top on vertical axis
          // and `end` is equivalent to right or bottom on horizontal axis.
          position: "start",
          labelInterpolationFnc: addThosandSeparator,
        },
        classNames: {
          line: "ct-line ct-line-anti-kr",
        },
        plugins: [
          Chartist.plugins.ctKrRectangles({
            above: scope.metric.targetOperator === "should_stay_above" ? true : false,
            target: scope.metric.target,
            critical: scope.metric.critical || scope.metric.critical === 0 ? scope.metric.critical : scope.metric.target,
          }),
        ],
      };

      const containerId = `metric-trend-${scope.id}`;
      setTimeout(() => {
        const container = document.getElementById(containerId);
        if (container) {
          new Chartist.Line(container, data, options);
        }
      }, 100);
    };

    // if there is not goal passed get it and then configure and open chart
    if (!scope.goal) {
      const goalFields = "dateFrom,dateTo,id,name,ownerIds,sessionId";

      this.goalService.getGoalWithFields(scope.metric.goalId, goalFields).then(
        (goal) => {
          scope.goal = goal;

          configureChartParamsAndDraw();
        },
        (error) => {
          console.error(error);
        }
      );
    } else {
      configureChartParamsAndDraw();
    }
  }

  public static factory(): IDirectiveFactory {
    const directive = (goalService: GoalService) => new TrendChartAntiKr(goalService);
    directive.$inject = ["GoalService"];

    return directive;
  }
}
